import type { CryptoCurrencyDataSource, FiatCurrency } from '@0xtorch/core'
import {
  type AvailableApiCategory,
  FailedToAnalyzeExchangeException,
} from '@pkg/basic'
import {
  getAnalyzingTargetApiAccounts,
  selectCryptoCurrencies,
} from '@pkg/database-portfolio'
import {
  type AssetFetchClient,
  fetchSymbolAssetMap,
} from '@pkg/datasource-client'
import {
  type ApiConfig,
  type AsterApiType,
  type BinanceApiType,
  type BitbankApiType,
  type BitgetApiType,
  type BybitApiType,
  type FetchApiData,
  type GateApiType,
  type HyperliquidApiType,
  type MexcApiType,
  asterApiConfigs,
  binanceApiConfigs,
  bitbankApiConfigs,
  bitgetApiConfigs,
  bybitApiConfigs,
  createAssetRecord,
  gateApiConfigs,
  hyperliquidApiConfigs,
  mexcApiConfigs,
} from '@pkg/exchange-api'
import type { OnUpdateApiProgressCallback, RunToDatabase } from '../types'
import { syncExchangeApiData } from './syncExchangeApiData'

type AnalyzeExchangeApiAccounts = (params: {
  accountIds: number[]
  assetFetchClient: AssetFetchClient
  category: AvailableApiCategory
  cryptoCurrencyDataSource: CryptoCurrencyDataSource
  fiat: FiatCurrency
  fiats: FiatCurrency[]
  portfolioStartTime: number
  proxy?: string
  getToken?: () => Promise<string | null>
  onError?: (error: unknown) => void
  onUpdateProgress?: OnUpdateApiProgressCallback
  readFromDatabase: RunToDatabase
  writeToDatabase: RunToDatabase
}) => Promise<void>

export const analyzeExchangeApiAccounts: AnalyzeExchangeApiAccounts = async ({
  accountIds,
  assetFetchClient,
  category,
  cryptoCurrencyDataSource,
  fiat,
  fiats,
  portfolioStartTime,
  proxy,
  getToken,
  onError,
  onUpdateProgress,
  readFromDatabase,
  writeToDatabase,
}) => {
  try {
    // DB から API 認証情報と account 毎の API 情報取得
    // 対象 API が無い場合は処理終了
    const targetApiAccounts = await readFromDatabase((database) =>
      getAnalyzingTargetApiAccounts({
        database,
        accountIds,
      }),
    )
    const size = targetApiAccounts.reduce(
      (sum, account) => sum + account.apis.length,
      0,
    )
    if (size === 0) {
      return
    }

    // DB から crypto currency 一括取得
    const cryptoes = await readFromDatabase((database) =>
      selectCryptoCurrencies({ database }),
    )

    // cryptoes と fiats から AssetRecord 生成
    const assets = createAssetRecord({
      cryptoes,
      fiats,
    })

    // asset client から symbolAssetMap 取得
    const symbolAssetMap = await fetchSymbolAssetMap({
      assetFetchClient,
      category,
    })

    // progress 更新
    if (onUpdateProgress) {
      onUpdateProgress({
        done: 0,
        size,
      })
    }

    let done = 0
    for (const account of targetApiAccounts) {
      for (const api of account.apis) {
        // progress 更新
        if (onUpdateProgress) {
          onUpdateProgress({
            key: {
              type: api.type,
              subType: api.subType,
            },
            range: 0,
            synced: 0,
          })
          await new Promise((resolve) => setTimeout(resolve, 0)) // Yield to event loop to avoid blocking
        }

        // ApiConfig , fetchApiData の取得
        const config = getApiConfig({
          category,
          type: api.type,
        })
        const fetchApiData = await getFetchApiData({
          category,
          type: api.type,
        })

        // API データ同期
        await syncExchangeApiData({
          accountId: account.id,
          apiId: api.id,
          apiKey: account.apiKey,
          apiPassphrase: account.apiPassphrase,
          apiSecret: account.apiSecret,
          assets,
          category,
          config,
          cryptoCurrencyDataSource,
          fiat,
          proxy,
          startTime: account.apiStartTime ?? portfolioStartTime,
          subType: api.subType,
          symbolAssetMap,
          fetchApiData,
          getToken,
          onError,
          onUpdateProgress,
          writeToDatabase,
        })

        // progress 更新
        done += 1
        if (onUpdateProgress) {
          onUpdateProgress({
            done,
          })
          await new Promise((resolve) => setTimeout(resolve, 0)) // Yield to event loop to avoid blocking
        }
      }
    }
  } catch (error) {
    throw new FailedToAnalyzeExchangeException(error, category)
  }
}

const getApiConfig = (params: {
  category: AvailableApiCategory
  type: string
}): ApiConfig => {
  const { category, type } = params

  switch (category) {
    case 'aster': {
      return asterApiConfigs[type as AsterApiType]
    }
    case 'binance': {
      return binanceApiConfigs[type as BinanceApiType]
    }
    case 'bitbank': {
      return bitbankApiConfigs[type as BitbankApiType]
    }
    case 'bitget': {
      return bitgetApiConfigs[type as BitgetApiType]
    }
    case 'bybit': {
      return bybitApiConfigs[type as BybitApiType]
    }
    case 'gate': {
      return gateApiConfigs[type as GateApiType]
    }
    case 'hyperliquid': {
      return hyperliquidApiConfigs[type as HyperliquidApiType]
    }
    case 'mexc': {
      return mexcApiConfigs[type as MexcApiType]
    }
    default: {
      throw new Error(`Unsupported API category: ${category satisfies never}`)
    }
  }
}

const getFetchApiData = async (params: {
  category: AvailableApiCategory
  type: string
}): Promise<FetchApiData> => {
  const { category, type } = params

  switch (category) {
    case 'aster': {
      const asterType = type as AsterApiType
      const { fetchAsterApiDataFuncs } = await import(
        './fetchAsterApiDataFuncs'
      )
      return fetchAsterApiDataFuncs[asterType]
    }
    case 'binance': {
      const binanceType = type as BinanceApiType
      const { fetchBinanceApiDataFuncs } = await import(
        './fetchBinanceApiDataFuncs'
      )
      return fetchBinanceApiDataFuncs[binanceType]
    }
    case 'bitbank': {
      const bitbankType = type as BitbankApiType
      const { fetchBitbankApiDataFuncs } = await import(
        './fetchBitbankApiDataFuncs'
      )
      return fetchBitbankApiDataFuncs[bitbankType]
    }
    case 'bitget': {
      const bitgetType = type as BitgetApiType
      const { fetchBitgetApiDataFuncs } = await import(
        './fetchBitgetApiDataFuncs'
      )
      return fetchBitgetApiDataFuncs[bitgetType]
    }
    case 'bybit': {
      const bybitType = type as BybitApiType
      const { fetchBybitApiDataFuncs } = await import(
        './fetchBybitApiDataFuncs'
      )
      return fetchBybitApiDataFuncs[bybitType]
    }
    case 'gate': {
      const gateType = type as GateApiType
      const { fetchGateApiDataFuncs } = await import('./fetchGateApiDataFuncs')
      return fetchGateApiDataFuncs[gateType]
    }
    case 'hyperliquid': {
      const hyperliquidType = type as HyperliquidApiType
      const { fetchHyperliquidApiDataFuncs } = await import(
        './fetchHyperliquidApiDataFuncs'
      )
      return fetchHyperliquidApiDataFuncs[hyperliquidType]
    }
    case 'mexc': {
      const mexcType = type as MexcApiType
      const { fetchMexcApiDataFuncs } = await import('./fetchMexcApiDataFuncs')
      return fetchMexcApiDataFuncs[mexcType]
    }
    default: {
      throw new Error(`Unsupported API category: ${category satisfies never}`)
    }
  }
}
